import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthActions, AuthStorageService, StateWithClientAuth } from '@spartacus/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private isLoggingOut = false;

  constructor(private store: Store<StateWithClientAuth>, private authStorageService: AuthStorageService, private router: Router) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check for various types of auth errors
        const isAuthError = this.isInvalidBearerTokenError(error) || this.isGeneric401AuthError(error);

        if (error.status === 401 && isAuthError && this.isApiCall(request.url) && !this.isLoggingOut) {
          this.isLoggingOut = true;
          // Clear auth storage by removing all auth-related keys
          this.clearAuthStorage();
          this.store.dispatch(new AuthActions.Logout());
          // Navigate to login page
          this.router.navigate(['/login']);
        }
        return throwError(error);
      })
    );
  }

  private isInvalidBearerTokenError(error: HttpErrorResponse): boolean {
    return error.error && error.error.errors && error.error.errors.some((err: any) => err.type === 'InvalidBearerTokenError');
  }

  private isGeneric401AuthError(error: HttpErrorResponse): boolean {
    // Check for other common auth error patterns that indicate token issues
    const isGeneric = error.error && (
      (error.error.error === 'invalid_token') ||
      (error.error.error === 'unauthorized') ||
      (error.error.error_description && (
        error.error.error_description.includes('token') ||
        error.error.error_description.includes('expired') ||
        error.error.error_description.includes('invalid')
      )) ||
      (typeof error.error === 'string' && (
        error.error.includes('token') ||
        error.error.includes('unauthorized') ||
        error.error.includes('expired')
      ))
    );
    return isGeneric;
  }

  private isApiCall(url: string): boolean {
    return url.includes('/occ/') && !url.includes('/oauth') && !url.includes('/authorizationserver');
  }

  private clearAuthStorage(): void {
    // Remove the Spartacus auth storage key
    try {
      // Spartacus stores auth data under a specific key
      const spartacusAuthKey = 'spartacus⚿⚿auth';
      localStorage.removeItem(spartacusAuthKey);
      sessionStorage.removeItem(spartacusAuthKey);

      // Also clear any OAuth library keys that might be stored separately
      const oauthKeys = ['access_token', 'refresh_token', 'token_type', 'expires_at', 'scope'];
      oauthKeys.forEach(key => {
        try {
          this.authStorageService.removeItem(key);
        } catch (error) {
          // Ignore errors for individual keys
        }
      });
    } catch (error) {
      console.warn('AuthErrorInterceptor: Failed to clear auth storage', error);
    }
  }
}